import type { Personality } from '../core/characters';
import { faceShapes } from './face';
import { handShapes } from './hands';
import { gripFingers, weaponHandholds } from './weaponGrip';
import type { Vec2 } from '../core/math';
import { PRIM_CAPSULE, PRIM_DISK, type Primitive } from './sdf/primitives';
import { playerGeometry } from '../sim/player/geometry';
import { heldWeaponAnchor, weaponPoint, type WeaponAnchor } from '../sim/weapons/geometry';

/**
 * Procedural stick figure (PLAN 4.6 / 4.11). Limbs are cosmetic while alive: the sim owns one
 * capsule, this module turns its state into a pose. Everything is in meters around the capsule
 * center (x, y); the figure is 1.8 m tall with feet at y - 0.9.
 */
export type FigurePose = {
  x: number;
  y: number;
  facing: number;
  ducking: boolean;
  grounded: boolean;
  wallSliding: boolean;
  vx: number;
  vy: number;
  aimX: number;
  aimY: number;
  punching: boolean;
  blocking: boolean;
  dead: boolean;
  phase: number;
  /** Held weapon, if any: arm(s) extend along aim and the hand anchors the weapon. */
  weapon?: { length: number; twoHanded: boolean; kind?: string; category?: string } | null;
  height?: number;
  weaponAnchor?: WeaponAnchor;
  wallDir?: number;
  /** Ticks since the figure was hit (for the flinch), 0 when not recently hit. */
  hurt?: number;
  recoil?: number;
  windingUp?: boolean;
  celebrating?: boolean;
  introducing?: boolean;
  personality?: Personality;
  parry?: number;
  stunned?: boolean;
  reducedMotion?: boolean;
  /** Cosmetic gaze and reactions, independent of weapon aim. */
  glance?: Vec2;
  closeCall?: boolean;
  pleased?: boolean;
};

/**
 * A loose joint: a spring-damped offset (body-local metres) from wherever the pose wants the part.
 * `tx/ty` remember the last target so a target that teleports (a pose change, a turn) becomes an
 * offset the spring works off, and the part visibly travels instead of snapping.
 */
export type Spring = { x: number; y: number; vx: number; vy: number; tx: number; ty: number; live: boolean };

export type LimbState = {
  hipSway: number;
  shoulderSway: number;
  /** 0–1 landing squash, decays every frame. */
  squash: number;
  takeoff: number;
  wasGrounded: boolean;
  /** Smoothed run-cycle phase so animation speed follows the actual velocity. */
  runPhase: number;
  runWeight: number;
  /** Eye blink timer (seconds until next blink; negative while blinking). */
  blink: number;
  /** Body velocity last frame, and this frame's change in it: the jolt that flops the limbs. */
  lastVx: number;
  lastVy: number;
  kickX: number;
  kickY: number;
  /** Frame time this state was advanced by (the springs integrate over it). */
  dt: number;
  torso: Spring;
  head: Spring;
  handMain: Spring;
  handOff: Spring;
  footL: Spring;
  footR: Spring;
  gaze: Spring;
};

export type FigureBuild = {
  /** Body silhouette (head + limbs) — one smooth-union group in the player color. */
  body: Primitive[];
  /** Near limbs paint above the torso while the whole silhouette stays available to physics previews. */
  nearArm: Primitive[];
  nearLeg: Primitive[];
  handInk: { main: Primitive[]; off: Primitive[] };
  grip: { fingers: Primitive[]; creases: Primitive[] };
  handAngles: { main: number; off: number };
  motion: { phase: number; vx: number; reduced: boolean };
  /** Eyes, drawn flat in a dark ink color on top of the body. */
  eyes: Primitive[];
  /** Main hand (aim / weapon hand) and off hand in world space. */
  handMain: Vec2;
  handOff: Vec2;
  /** Gameplay weapon anchor; visible palms wrap its handle below this point. */
  weaponAnchor: { x: number; y: number; angle: number; flip: boolean };
  head: { x: number; y: number; r: number };
  look: Vec2;
  joints: { hip: Vec2; shoulder: Vec2; elbowMain: Vec2; elbowOff: Vec2; kneeL: Vec2; kneeR: Vec2; footL: Vec2; footR: Vec2 };
};

export const FIGURE = {
  // A large expressive head, with enough torso and limb separation to read the pose and details.
  // These are visual proportions; gameplay keeps its existing capsule and weapon anchors.
  height: 1.8,
  headR: 0.31,
  torsoR: 0.145,
  armR: 0.085,
  legR: 0.095,
  handR: 0.105,
  upperArm: 0.32,
  foreArm: 0.32,
  thigh: 0.36,
  shin: 0.36,
  smoothK: 0.06,
} as const;

function spring(): Spring {
  return { x: 0, y: 0, vx: 0, vy: 0, tx: 0, ty: 0, live: false };
}

export function emptyLimbState(): LimbState {
  return {
    hipSway: 0,
    shoulderSway: 0,
    squash: 0,
    takeoff: 0,
    wasGrounded: true,
    runPhase: 0,
    runWeight: 0,
    blink: 2.5,
    lastVx: 0,
    lastVy: 0,
    kickX: 0,
    kickY: 0,
    dt: 1 / 60,
    torso: spring(),
    head: spring(),
    handMain: spring(),
    handOff: spring(),
    footL: spring(),
    footR: spring(),
    gaze: spring(),
  };
}

export function secondaryFromVelocity(prev: LimbState, vx: number, vy: number, grounded = true, dt = 1 / 60): LimbState {
  if (dt <= 0) return { ...prev, dt: 0, kickX: 0, kickY: 0 };
  const landed = grounded && !prev.wasGrounded;
  const takeoff = !grounded && prev.wasGrounded && vy > 1 ? 1 : prev.takeoff * Math.pow(0.75, dt * 60);
  const squash = landed ? Math.min(1, 0.25 + Math.abs(prev.lastVy) / 18) : prev.squash * Math.pow(0.78, dt * 60);
  const speed = Math.abs(vx);
  const runPhase = prev.runPhase + (grounded && speed > 0.4 ? speed * 2.4 * dt : 0);
  let blink = prev.blink - dt;
  if (blink < -0.12) blink = 2 + Math.random() * 3.5;
  // A frame that is not a sim tick repeats the velocity: no jolt. A stall (tab hidden) is capped so
  // the figure does not explode on return.
  const step = Math.min(dt, 0.05);
  return {
    ...prev,
    hipSway: prev.hipSway + (vx * 0.05 - prev.hipSway) * (1 - Math.pow(0.8, dt * 60)),
    shoulderSway: prev.shoulderSway + (-vx * 0.032 + vy * 0.016 - prev.shoulderSway) * (1 - Math.pow(0.75, dt * 60)),
    squash,
    takeoff,
    wasGrounded: grounded,
    runPhase,
    runWeight: prev.runWeight + ((grounded ? Math.min(1, speed / 5) : 0) - prev.runWeight) * (1 - Math.exp(-dt * 12)),
    blink,
    lastVx: vx,
    lastVy: vy,
    kickX: clamp(vx - prev.lastVx, -20, 20),
    kickY: clamp(vy - prev.lastVy, -20, 20),
    dt: step,
  };
}

/** How loose a joint is: natural frequency (rad/s), damping ratio, jolt gain and max travel (m). */
type Looseness = { omega: number; zeta: number; jolt: number; max: number };
const LOOSE: Looseness = { omega: 16, zeta: 0.28, jolt: 0.32, max: 0.34 };
const SWINGING: Looseness = { omega: 24, zeta: 0.4, jolt: 0.26, max: 0.3 };
const FIRM: Looseness = { omega: 40, zeta: 0.75, jolt: 0.1, max: 0.12 };
const PLANTED: Looseness = { omega: 42, zeta: 0.85, jolt: 0.04, max: 0.08 };
const DANGLING: Looseness = { omega: 13, zeta: 0.3, jolt: 0.24, max: 0.3 };
const BOBBLE: Looseness = { omega: 17, zeta: 0.3, jolt: 0.1, max: 0.12 };
const SWAY: Looseness = { omega: 14, zeta: 0.32, jolt: 0.06, max: 0.12 };
const GAZE: Looseness = { omega: 18, zeta: 1, jolt: 0, max: 2 };
const GAIT = {
  confident: { cadence: 0.95, stride: 1.03, lift: 0.95, swing: 0.85, weight: 1 },
  nervous: { cadence: 1.18, stride: 0.87, lift: 1.12, swing: 0.7, weight: 0.8 },
  scrappy: { cadence: 1.06, stride: 1.08, lift: 1.1, swing: 1.15, weight: 1.15 },
  sleepy: { cadence: 0.86, stride: 0.95, lift: 0.68, swing: 1.2, weight: 1.05 },
} as const;

/**
 * Advance a loose joint toward its target (body-local) and return where it actually is. The
 * offset absorbs any change in the target, gets kicked by the body's velocity change, then
 * relaxes as a damped spring (semi-implicit Euler, substepped so stiff joints stay stable).
 */
function settle(s: Spring, tx: number, ty: number, k: Looseness, sec: LimbState, kickScale = 1): Vec2 {
  if (!s.live) {
    s.tx = tx;
    s.ty = ty;
    s.live = true;
  }
  s.x += s.tx - tx;
  s.y += s.ty - ty;
  s.tx = tx;
  s.ty = ty;
  s.vx -= sec.kickX * k.jolt * kickScale;
  s.vy -= sec.kickY * k.jolt * kickScale;
  const steps = Math.max(1, Math.ceil(sec.dt / (1 / 120)));
  const h = sec.dt / steps;
  const stiff = k.omega * k.omega;
  const damp = 2 * k.zeta * k.omega;
  for (let i = 0; i < steps; i++) {
    s.vx += (-stiff * s.x - damp * s.vx) * h;
    s.vy += (-stiff * s.y - damp * s.vy) * h;
    s.x += s.vx * h;
    s.y += s.vy * h;
  }
  const m = Math.hypot(s.x, s.y);
  if (m > k.max) {
    const f = k.max / m;
    s.x *= f;
    s.y *= f;
    s.vx *= f;
    s.vy *= f;
  }
  return { x: tx + s.x, y: ty + s.y };
}

export function buildFigure(pose: FigurePose, sec: LimbState = emptyLimbState()): FigureBuild {
  const F = FIGURE;
  const facing = pose.facing >= 0 ? 1 : -1;
  const feetY = pose.y - F.height / 2;
  const aimLen = Math.hypot(pose.aimX, pose.aimY) || 1;
  const aim = { x: pose.aimX / aimLen, y: pose.aimY / aimLen };
  const aimAngle = Math.atan2(aim.y, aim.x);
  const aimSide = aim.x >= 0 ? 1 : -1;
  const personality = pose.personality ?? 'confident';
  const gait = GAIT[personality];
  const kind = pose.weapon?.kind ?? '';
  const heavy = pose.weapon?.category === 'explosive' || kind.includes('minigun');
  const shotgun = kind.includes('shotgun') || kind === 'sawed-off';
  const blade = pose.weapon?.category === 'melee';
  const showOff = !!pose.celebrating || !!pose.introducing;
  const idle = pose.grounded && Math.abs(pose.vx) < 0.4 && !pose.blocking && !pose.punching && !pose.windingUp;
  const breath = idle && !pose.reducedMotion ? Math.sin(pose.phase * (personality === 'nervous' ? 5 : 2.5)) * 0.015 : 0;
  const hurt = Math.max(0, pose.hurt ?? 0);
  const flinch = hurt > 0 ? Math.min(1, hurt / 8) : 0;

  // Vertical stack, compressed when ducking and squashed on landing (scaled about the feet).
  const duck = playerGeometry(F.height, pose.ducking).poseScale;
  const squashY = 1 - sec.squash * (pose.reducedMotion ? 0.12 : 0.23 * gait.weight);
  const stretchY = 1 + (!pose.reducedMotion ? sec.takeoff * 0.055 : 0);
  const scaleY = duck * squashY * stretchY;
  const lift = (h: number) => feetY + h * scaleY;
  // Keep the longer idle legs nearly straight rather than resting in a half-squat.
  const hipH = 0.69;
  const gaitPhase = sec.runPhase * gait.cadence;
  const bounce = pose.reducedMotion ? 0 : Math.sin(gaitPhase * 2) * sec.runWeight * 0.022 * gait.lift;
  const shoulderH = 1.10 + breath + bounce - (pose.blocking ? 0.065 : heavy ? 0.045 : personality === 'sleepy' ? 0.045 : 0);
  const headH = 1.47 + breath + bounce * 0.65 + (showOff ? 0.04 : 0) - (pose.blocking ? 0.08 : heavy ? 0.04 : personality === 'sleepy' ? 0.035 : 0);
  const recoil = (pose.recoil ?? 0) * (heavy ? 1.3 : shotgun ? 1.15 : 0.65);
  const posture = idle ? (personality === 'confident' ? -0.025 : personality === 'nervous' ? -0.04 : personality === 'scrappy' ? 0.035 : 0.04) * aimSide : 0;
  const leanX = clamp(pose.vx * 0.018, -0.17, 0.17) * (pose.grounded ? 1 : 0.5) + sec.shoulderSway * 0.45
    - flinch * 0.14 * aimSide - recoil * 0.14 * aimSide + posture
    - (pose.windingUp ? aimSide * 0.08 : 0) - (pose.blocking ? aimSide * 0.055 : 0)
    + (pose.weapon?.twoHanded ? aim.x * 0.06 : 0) + (pose.parry ?? 0) * aimSide * 0.07;
  const slideOffset = pose.wallSliding ? -0.12 * facing : 0;

  // Loose joints work in body-local space so moving with the body costs nothing; only jolts (the
  // body's velocity changing) and pose changes set them swinging.
  const local = (p: Vec2): Vec2 => ({ x: p.x - pose.x, y: p.y - pose.y });
  const world = (p: Vec2): Vec2 => ({ x: p.x + pose.x, y: p.y + pose.y });
  const loose = (s: Spring, target: Vec2, k: Looseness, kickScale = 1): Vec2 => {
    const t = local(target);
    return world(settle(s, t.x, t.y, k, sec, kickScale));
  };

  const hip = { x: pose.x + sec.hipSway * 0.6 + slideOffset, y: lift(hipH - sec.runWeight * 0.035) };
  // The torso whips a little after the hips, and the head bobbles after the torso.
  const shoulder = loose(sec.torso, { x: pose.x + leanX + slideOffset * 0.6, y: lift(shoulderH) }, SWAY);
  const torsoLag = { x: shoulder.x - (pose.x + leanX + slideOffset * 0.6), y: shoulder.y - lift(shoulderH) };
  const lookUpLean = Math.max(0, aim.y - 0.2) * (pose.weapon ? 0.32 : pose.punching ? 0.16 : 0);
  const headTarget = { x: pose.x + leanX * 1.5 + slideOffset * 0.3 + torsoLag.x * 1.4 - aimSide * lookUpLean, y: lift(headH) + torsoLag.y };
  const head = loose(sec.head, headTarget, BOBBLE);
  // Landing compression belongs in the body; keep the chin above the shoulder as the head settles.
  head.y = Math.max(head.y, shoulder.y + (F.headR + 0.045) * Math.min(1, duck + 0.2));

  // Legs
  const run = pose.grounded && Math.abs(pose.vx) > 0.4 ? gaitPhase : null;
  const stride = clamp(Math.abs(pose.vx) / 8, 0, 1) * gait.stride;
  let footL: Vec2;
  let footR: Vec2;
  let kneeDirL = facing;
  let kneeDirR = facing;
  if (pose.ducking) {
    footL = { x: pose.x - 0.3, y: feetY };
    footR = { x: pose.x + 0.3, y: feetY };
    kneeDirL = -1;
    kneeDirR = 1;
  } else if (pose.wallSliding) {
    const wall = pose.wallDir ?? facing;
    footL = { x: pose.x + wall * 0.2, y: feetY + 0.1 };
    footR = { x: pose.x + wall * 0.08, y: feetY + 0.26 };
    kneeDirL = -wall;
    kneeDirR = -wall;
  } else if (!pose.grounded) {
    if (pose.vy > 1.5) {
      // rising: tuck
      footL = { x: pose.x + facing * 0.16, y: feetY + 0.28 };
      footR = { x: pose.x - facing * 0.1, y: feetY + 0.14 };
    } else {
      // falling: legs trail, slightly spread
      footL = { x: pose.x + facing * 0.24, y: feetY + 0.08 };
      footR = { x: pose.x - facing * 0.2, y: feetY + 0.02 };
    }
  } else if (run !== null) {
    // Separate the hip attachments and lift the passing foot, so the legs do not read as an X.
    const stepFoot = (phase: number, side: number): Vec2 => {
      const cycle = ((phase / (Math.PI * 2)) % 1 + 1) % 1;
      const stance = cycle < 0.52;
      const t = stance ? cycle / 0.52 : (cycle - 0.52) / 0.48;
      const swing = t * t * (3 - 2 * t);
      return { x: hip.x + side * F.torsoR * 0.5 + facing * (stance ? 1 - 2 * t : -1 + 2 * swing) * 0.34 * stride,
        y: feetY + (stance ? 0 : Math.sin(t * Math.PI) * 0.31 * stride * gait.lift) };
    };
    footL = stepFoot(run, -1);
    footR = stepFoot(run + Math.PI, 1);
  } else {
    const stance = heavy || pose.blocking || personality === 'scrappy' ? 0.21 : 0.16;
    footL = { x: pose.x - stance, y: feetY };
    footR = { x: pose.x + stance, y: feetY };
    kneeDirL = facing;
    kneeDirR = facing;
  }
  // Feet plant firmly on the ground but dangle in the air; a planted foot never sinks into the floor.
  const legReach = F.thigh + F.shin;
  const hipL = { x: hip.x - F.torsoR * 0.45, y: hip.y };
  const hipR = { x: hip.x + F.torsoR * 0.45, y: hip.y };
  const footK = pose.grounded ? PLANTED : DANGLING;
  footL = clampReach(hipL, loose(sec.footL, footL, footK), legReach);
  footR = clampReach(hipR, loose(sec.footR, footR, footK, 0.8), legReach);
  if (pose.grounded && !pose.wallSliding) {
    footL.y = idle || pose.blocking ? feetY : Math.max(footL.y, feetY);
    footR.y = idle || pose.blocking ? feetY : Math.max(footR.y, feetY);
  }
  const kneeL = solveJoint(hipL, footL, F.thigh, F.shin, kneeDirL);
  const kneeR = solveJoint(hipR, footR, F.thigh, F.shin, kneeDirR);

  // Arms
  const holding = !!pose.weapon;
  let handMain: Vec2;
  let handOff: Vec2;
  let elbowDirMain = -aimSide;
  let elbowDirOff = -aimSide;
  const reach = F.upperArm + F.foreArm;
  if (pose.dead) {
    handMain = { x: shoulder.x + 0.2, y: shoulder.y - 0.5 };
    handOff = { x: shoulder.x - 0.2, y: shoulder.y - 0.5 };
  } else if (showOff && !holding) {
    // A raised fist beside the temple, with the elbow out, keeps the big head and grin clear.
    handMain = { x: shoulder.x + aimSide * 0.55, y: shoulder.y + 0.4 };
    handOff = { x: shoulder.x - aimSide * 0.55, y: shoulder.y + 0.4 };
    elbowDirMain = -aimSide;
    elbowDirOff = aimSide;
  } else if (pose.windingUp && !holding) {
    handMain = { x: shoulder.x - aim.x * 0.22, y: shoulder.y + 0.15 };
    handOff = { x: shoulder.x + aim.x * 0.25, y: shoulder.y - 0.12 };
  } else if (pose.blocking) {
    const perp = { x: -aim.y, y: aim.x };
    handMain = { x: shoulder.x + aim.x * 0.42 + perp.x * 0.14, y: shoulder.y + aim.y * 0.42 + perp.y * 0.14 };
    handOff = { x: shoulder.x + aim.x * 0.38 - perp.x * 0.14, y: shoulder.y + aim.y * 0.38 - perp.y * 0.14 };
    elbowDirMain = -aimSide;
    elbowDirOff = aimSide;
  } else if (pose.punching) {
    const ext = 0.62 + 0.05;
    handMain = { x: shoulder.x + aim.x * ext, y: shoulder.y + aim.y * ext };
    handOff = { x: shoulder.x - aim.x * 0.22, y: shoulder.y - 0.28 };
    elbowDirOff = aimSide;
  } else if (holding) {
    const w = pose.weapon!;
    const ext = w.twoHanded ? 0.52 : 0.58;
    handMain = { x: shoulder.x + aim.x * ext, y: shoulder.y + aim.y * ext };
    if (w.twoHanded) {
      const perp = { x: -aim.y, y: aim.x };
      const fwd = Math.min(reach * 0.98, ext + Math.min(0.32, w.length * 0.45));
      handOff = { x: shoulder.x + aim.x * fwd - perp.x * 0.06 * aimSide, y: shoulder.y + aim.y * fwd - perp.y * 0.06 * aimSide };
      elbowDirOff = aimSide;
    } else {
      handOff = idleHand(shoulder, -facing, run === null ? null : run + Math.PI, stride, pose);
      elbowDirOff = -facing;
    }
  } else if (pose.wallSliding) {
    const wall = pose.wallDir ?? facing;
    handMain = { x: pose.x + wall * 0.36, y: shoulder.y + 0.26 };
    handOff = { x: shoulder.x - wall * 0.18, y: shoulder.y - 0.45 };
    elbowDirMain = -wall;
    elbowDirOff = wall;
  } else if (!pose.grounded) {
    // Airborne: elbows bend down and out, keeping the forearms below the face.
    const up = pose.vy > 1.5;
    handMain = { x: shoulder.x + facing * 0.5, y: shoulder.y + (up ? 0.12 : 0.26) };
    handOff = { x: shoulder.x - facing * 0.5, y: shoulder.y + (up ? 0.04 : 0.22) };
    elbowDirMain = -facing;
    elbowDirOff = facing;
  } else {
    handMain = idleHand(shoulder, facing, run, stride, pose);
    handOff = idleHand(shoulder, -facing, run === null ? null : run + Math.PI, stride, pose);
    // Keep hanging and running elbows outside the chest so markings and the arm read separately.
    elbowDirMain = facing;
    elbowDirOff = -facing;
  }
  if (showOff && !pose.dead) {
    const wave = pose.reducedMotion ? 0 : Math.sin(pose.phase * 8) * 0.09;
    handOff = personality === 'sleepy'
      ? { x: hip.x - aimSide * 0.27, y: hip.y + 0.06 }
      : personality === 'scrappy'
        ? { x: shoulder.x - aimSide * 0.54, y: shoulder.y + 0.12 + wave }
        : personality === 'nervous'
          ? { x: shoulder.x - aimSide * (0.51 + wave * 0.3), y: shoulder.y + 0.25 }
          : { x: shoulder.x - aimSide * (0.54 + wave * 0.25), y: shoulder.y + 0.4 };
    elbowDirOff = aimSide;
  }
  // Arms hang off the torso's edges, main arm on the aim side.
  const armSide = pose.dead || pose.wallSliding || (!pose.grounded && !holding && !pose.punching && !pose.blocking) ? facing : aimSide;
  const shoulderMain = { x: shoulder.x + armSide * F.torsoR * 0.7, y: shoulder.y + 0.02 };
  const shoulderOff = { x: shoulder.x - armSide * F.torsoR * 0.7, y: shoulder.y + 0.02 };
  // A held grip is shared with the sim. Keep it exact through aim changes and landing jolts;
  // the elbow and free limbs still follow the cosmetic torso springs.
  if (idle && (!holding || !pose.weapon?.twoHanded) && !showOff) {
    if (personality === 'confident') handOff = { x: hip.x - facing * 0.23, y: hip.y + 0.08 };
    if (personality === 'nervous') handOff = { x: shoulder.x + aimSide * 0.18, y: shoulder.y - 0.16 };
    if (personality === 'scrappy') { handMain.y += 0.2; handOff.y += 0.17; }
  }
  if (blade && !showOff && !pose.blocking) handOff = { x: shoulder.x - aimSide * 0.35, y: shoulder.y - 0.12 };
  if (pose.parry && pose.blocking) { handOff.x += aim.x * pose.parry * 0.16; handOff.y += aim.y * pose.parry * 0.16; }
  const busyMain = holding || pose.punching || pose.blocking;
  const busyOff = (holding && pose.weapon!.twoHanded) || pose.blocking;
  const freeK = showOff ? PLANTED : !pose.grounded ? LOOSE : run !== null ? SWINGING : LOOSE;
  const grip = pose.weaponAnchor ?? heldWeaponAnchor({ ...pose, twoHanded: pose.weapon?.twoHanded ?? false });
  const contacts = weaponHandholds(kind, pose.weapon?.length ?? 0);
  const supporting = holding && pose.weapon!.twoHanded && !pose.blocking && !showOff && !pose.dead;
  handMain = holding && !pose.dead
    ? weaponPoint(contacts.main, grip)
    : clampReach(shoulderMain, loose(sec.handMain, handMain, busyMain ? FIRM : freeK), reach);
  handOff = supporting
    ? weaponPoint(contacts.support, grip)
    : clampReach(shoulderOff, loose(sec.handOff, handOff, busyOff ? FIRM : freeK, 1.15), reach);
  if (run !== null && !showOff && !pose.dead && !pose.blocking && !pose.punching && !pose.windingUp) {
    // A start/turn jolt can overshoot the gait target; keep free palms on their side of the body.
    if (!holding) handMain.x = shoulder.x + armSide * Math.max(0.18, (handMain.x - shoulder.x) * armSide);
    if (!supporting) handOff.x = shoulder.x - armSide * Math.max(0.18, (shoulder.x - handOff.x) * armSide);
  }
  const faceScale = F.headR / 0.36;
  const faceCenter = { x: head.x + aim.x * 0.12 * faceScale, y: head.y };
  const facePoints = [[-0.075, -0.105], [0.075, -0.105], [-0.08, 0.035], [0.08, 0.035], [0, 0.14]]
    .map(([x, y]) => ({ x: faceCenter.x + x! * faceScale, y: faceCenter.y + y! * faceScale }));
  // Free hands take an outside route after spring overshoot; held grips remain exact.
  if (!holding && !pose.dead && !pose.blocking && !pose.punching) handMain = clearFace(handMain, shoulderMain, faceCenter, armSide, reach);
  if (!busyOff && !pose.dead && !pose.blocking) handOff = clearFace(handOff, shoulderOff, faceCenter, -armSide, reach);
  const elbowMain = armJoint(shoulderMain, handMain, elbowDirMain, facePoints);
  const elbowOff = armJoint(shoulderOff, handOff, elbowDirOff, facePoints);
  const closed = pose.punching || pose.blocking || pose.windingUp || showOff || personality === 'scrappy';
  const mainHand = handShapes(handMain, elbowMain, holding ? 'grip' : closed ? 'fist' : 'relaxed', armSide, pose.punching, F.handR / 0.12);
  const offHand = handShapes(handOff, elbowOff, holding && pose.weapon!.twoHanded && !showOff ? 'grip' : closed ? 'fist' : 'relaxed', -armSide, false, F.handR / 0.12);
  const fingers = holding && !pose.dead ? [gripFingers(contacts.main, grip)] : [];
  if (supporting) fingers.push(gripFingers(contacts.support, grip, contacts.supportUnderBarrel));

  const body: Primitive[] = [
    disk(head, F.headR),
    cap(shoulder, hip, F.torsoR),
    cap(hipL, kneeL, F.legR),
    cap(kneeL, footL, F.legR * 0.92),
    cap(hipR, kneeR, F.legR),
    cap(kneeR, footR, F.legR * 0.92),
    cap(shoulderOff, elbowOff, F.armR),
    cap(elbowOff, handOff, F.armR * 0.95),
    cap(shoulderMain, elbowMain, F.armR),
    cap(elbowMain, handMain, F.armR * 0.95),
    mainHand.palm,
    offHand.palm,
    disk(footL, F.legR * 1.05),
    disk(footR, F.legR * 1.05),
    mainHand.thumb,
    offHand.thumb,
  ];

  const gazeTarget = pose.glance && !holding && idle && !showOff ? pose.glance : aim;
  const gaze = pose.reducedMotion ? gazeTarget : settle(sec.gaze, gazeTarget.x, gazeTarget.y, GAZE, sec, 0);
  const eyes = faceShapes({ x: head.x, y: head.y, headRadius: F.headR, aimX: gaze.x, aimY: gaze.y, personality,
    blink: sec.blink < 0 || sec.squash > 0.65, hurt: hurt > 0, stunned: pose.stunned, dead: pose.dead,
    surprised: (!pose.grounded && pose.vy < -7) || pose.closeCall,
    focused: pose.blocking || pose.punching || pose.windingUp || (pose.recoil ?? 0) > 0.2,
    happy: showOff || pose.pleased || (pose.parry ?? 0) > 0,
  });

  return {
    body,
    nearArm: [body[8]!, body[9]!, body[10]!, body[14]!],
    nearLeg: facing >= 0 ? [body[4]!, body[5]!, body[13]!] : [body[2]!, body[3]!, body[12]!],
    eyes,
    handInk: { main: holding ? [] : mainHand.crease, off: supporting ? [] : offHand.crease },
    grip: { fingers: fingers.flatMap((hand) => hand.fingers), creases: fingers.map((hand) => hand.crease) },
    handAngles: { main: mainHand.angle, off: offHand.angle },
    motion: { phase: pose.phase, vx: pose.vx, reduced: !!pose.reducedMotion },
    handMain,
    handOff,
    weaponAnchor: holding && !pose.dead ? grip : { x: handMain.x, y: handMain.y, angle: aimAngle, flip: aim.x < 0 },
    head: { x: head.x, y: head.y, r: F.headR },
    look: { x: clamp(gaze.x, -1, 1) * 0.12 * faceScale, y: clamp(gaze.y, -0.7, 0.7) * 0.09 * faceScale },
    joints: { hip, shoulder, elbowMain, elbowOff, kneeL, kneeR, footL, footR },
  };
}

/** Legacy accessor used by tests and the debug view: body primitives only. */
export function poseToPrimitives(pose: FigurePose, secondary: Partial<LimbState> = {}): Primitive[] {
  return buildFigure(pose, { ...emptyLimbState(), ...secondary }).body.slice(0, 16);
}

function idleHand(shoulder: Vec2, side: number, run: number | null, stride: number, pose: FigurePose): Vec2 {
  if (run !== null) {
    const gait = GAIT[pose.personality ?? 'confident'];
    const swing = Math.sin(run) * 0.13 * stride * gait.swing;
    const tuck = pose.personality === 'nervous' ? 0.045 : pose.personality === 'scrappy' ? 0.085 : 0;
    return { x: shoulder.x + side * 0.29 + swing * pose.facing, y: shoulder.y - 0.52 + Math.max(0, Math.sin(run)) * 0.07 + tuck };
  }
  const bob = Math.sin(pose.phase * 2) * 0.01;
  return { x: shoulder.x + side * 0.3, y: shoulder.y - 0.58 + bob };
}

function segmentDistance(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x, dy = b.y - a.y;
  const t = clamp(((p.x - a.x) * dx + (p.y - a.y) * dy) / Math.max(1e-8, dx * dx + dy * dy), 0, 1);
  return Math.hypot(p.x - a.x - t * dx, p.y - a.y - t * dy);
}

function clearFace(hand: Vec2, shoulder: Vec2, face: Vec2, side: number, reach: number): Vec2 {
  if (Math.hypot(hand.x - face.x, hand.y - face.y) >= 0.31) return hand;
  return clampReach(shoulder, { x: face.x + side * 0.37, y: hand.y }, reach);
}

/** Pick the bend that preserves facial features, with a margin to avoid switching on tiny changes. */
function armJoint(shoulder: Vec2, hand: Vec2, direction: number, face: Vec2[]): Vec2 {
  const preferred = solveJoint(shoulder, hand, FIGURE.upperArm, FIGURE.foreArm, direction, true);
  const clearance = (elbow: Vec2) => Math.min(...face.map((point) => Math.min(segmentDistance(point, shoulder, elbow), segmentDistance(point, elbow, hand))));
  const score = clearance(preferred);
  if (score >= 0.19) return preferred;
  const alternate = solveJoint(shoulder, hand, FIGURE.upperArm, FIGURE.foreArm, -direction, true);
  return clearance(alternate) > score + 0.025 ? alternate : preferred;
}

/**
 * Two-bone IK: joint position for a limb from `a` to `b` with segment lengths l1/l2.
 * `dir` picks the bend side (+1 bends toward +x for vertical limbs; for arms the bend goes
 * along the perpendicular of the shoulder→hand direction).
 */
function solveJoint(a: Vec2, b: Vec2, l1: number, l2: number, dir: number, arm = false): Vec2 {
  let dx = b.x - a.x;
  let dy = b.y - a.y;
  let d = Math.hypot(dx, dy);
  const max = l1 + l2 - 1e-3;
  if (d > max) {
    dx *= max / d;
    dy *= max / d;
    d = max;
  }
  if (d < 1e-4) return { x: a.x + l1 * 0.5, y: a.y - l1 * 0.5 };
  const cosA = clamp((l1 * l1 + d * d - l2 * l2) / (2 * l1 * d), -1, 1);
  const ang = Math.acos(cosA);
  const base = Math.atan2(dy, dx);
  // Perpendicular choice: legs bend so the knee points along `dir` (x axis); arms bend so the elbow
  // sits on the `dir` side of the shoulder→hand line, which reads as "elbow out".
  let side: number;
  if (arm) side = dir >= 0 ? 1 : -1;
  else side = (dir >= 0 ? 1 : -1) * (dy <= 0 ? 1 : -1);
  const jointAng = base + side * ang;
  return { x: a.x + Math.cos(jointAng) * l1, y: a.y + Math.sin(jointAng) * l1 };
}

function clampReach(origin: Vec2, target: Vec2, reach: number): Vec2 {
  const dx = target.x - origin.x;
  const dy = target.y - origin.y;
  const d = Math.hypot(dx, dy);
  if (d <= reach) return target;
  return { x: origin.x + (dx / d) * reach, y: origin.y + (dy / d) * reach };
}

function disk(c: Vec2, r: number): Primitive {
  return { kind: PRIM_DISK, ax: c.x, ay: c.y, bx: c.x, by: c.y, r };
}
function cap(a: Vec2, b: Vec2, r: number): Primitive {
  return { kind: PRIM_CAPSULE, ax: a.x, ay: a.y, bx: b.x, by: b.y, r };
}
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}
